import { useEffect, useRef, useState, useCallback } from "react";
import { removeStoredToken } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * useStaffIdleTimeout — logout staff sessions after N minutes of inactivity.
 * Refreshes the staff JWT on activity (throttled). Any touchstart/mousemove/keydown
 * resets the timer. Warns 15 s before expiry.
 */
export function useStaffIdleTimeout(minutes, apiRefresher) {
    const nav = useNavigate();
    const [warning, setWarning] = useState(false);
    const timerRef = useRef(null);
    const warnRef = useRef(null);
    const lastRefreshRef = useRef(Date.now());

    const doLogout = useCallback(() => {
        removeStoredToken();
        sessionStorage.removeItem("staff_timeout_min");
        toast.info("Sessione staff terminata per inattività");
        nav("/", { replace: true });
    }, [nav]);

    const reset = useCallback(() => {
        setWarning(false);
        clearTimeout(timerRef.current);
        clearTimeout(warnRef.current);
        const ms = Math.max(30, minutes * 60) * 1000;
        warnRef.current = setTimeout(() => setWarning(true), ms - 15000);
        timerRef.current = setTimeout(doLogout, ms);
        // Throttled backend refresh
        const now = Date.now();
        if (apiRefresher && now - lastRefreshRef.current > 30000) {
            lastRefreshRef.current = now;
            apiRefresher();
        }
    }, [minutes, doLogout, apiRefresher]);

    useEffect(() => {
        if (!minutes) return;
        reset();
        const evts = ["touchstart", "mousemove", "keydown", "scroll", "click"];
        evts.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        return () => {
            clearTimeout(timerRef.current);
            clearTimeout(warnRef.current);
            evts.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [minutes, reset]);

    return warning;
}
